import React, { useState } from 'react'

import { Box, RadioButton, Text, theme, useBreakpoint } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { F0AvatarModule } from '@factorialco/f0-react'
import { Cross } from '@factorialco/f0-react/icons/app'
import {
  InAppCommsButton,
  InAppCommsConditionalOption,
  InAppCommsForm,
  Maybe,
} from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import CommsButton from 'modules/in_app_comms/components/CommsButton'
import { moduleAvailableToModuleId } from 'modules/upsell/lib/moduleIcons'

interface CustomerFormProps {
  content: Maybe<InAppCommsForm> | undefined
  componentName: string
  onDismiss: () => void
}

interface FormOption {
  value: string
  internalValue?: string
}

const CustomerForm = ({ content, componentName, onDismiss }: CustomerFormProps) => {
  const { isMobile } = useBreakpoint()
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [responses, setResponses] = useState<Record<string, string>>({})

  const handleClose = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    onDismiss()
    track(trackingEvents.inAppComms.dismissInAppCommsComponent, {
      component: 'in_app_comms_customer_form',
      componentName,
    })
  }

  const triggerEvent = (action: string, stepId: string, selectedOption?: string) => {
    track(trackingEvents.inAppComms.clickInAppCommsComponent, {
      componentType: 'in_app_comms_customer_form',
      componentName,
      buttonAction: action,
      stepId,
      selectedOption,
    })
  }

  useUnsafeEffect(() => {
    if (!content?.steps.length) return
    track(trackingEvents.inAppComms.viewInAppCommsComponent, {
      component: 'in_app_comms_customer_form',
      componentName,
    })
  }, [content?.steps, componentName])

  if (!content || !content.steps || content.steps.length === 0) {
    return null
  }

  const parseOptions = (optionsString: string): FormOption[] => {
    try {
      if (!optionsString || optionsString === '[]') return []
      // Convert Ruby hash format to valid JSON
      const jsonString = optionsString.replace(/=>/g, ':').replace(/([a-zA-Z0-9_]+):/g, '"$1":')
      return JSON.parse(jsonString)
    } catch (error) {
      return []
    }
  }

  const currentStep = content.steps[currentStepIndex]

  if (!currentStep) return null

  const options = parseOptions(currentStep.options || '[]')
  const selectedValue = responses[currentStep.stepId]

  const handleOptionChange = (value: string) => {
    setResponses((prev) => ({ ...prev, [currentStep.stepId]: value }))
  }

  const handleConditionalButtonClick = (button: InAppCommsButton) => {
    triggerEvent(`Click on conditional option`, currentStep.stepId, selectedValue)

    if (button.conditionalOptions) {
      const selectedVal = responses[currentStep.stepId]
      if (!selectedVal) return
      const match = button.conditionalOptions.find((opt: InAppCommsConditionalOption) => {
        const conditions = opt.conditionalIf.split(',').map((c: string) => c.trim())
        return conditions.includes(selectedVal)
      })

      if (match) {
        const nextStepIndex = content.steps.findIndex(
          (step) => step.stepId === match.conditionalGoTo
        )
        if (nextStepIndex !== -1) {
          setCurrentStepIndex(nextStepIndex)
        }
      } else {
        // No match: go to the next step (if there is one)
        if (currentStepIndex < content.steps.length - 1) {
          setCurrentStepIndex(currentStepIndex + 1)
        } else {
          // Optionally, dismiss if it's the last step
          onDismiss()
        }
      }
    }
  }

  const renderButton = (button: InAppCommsButton, index: number) => {
    const isLastStep = currentStepIndex === content.steps.length - 1
    const isCloseButton = button.type === 'CommsCloseButtonRecord'
    const variant = isLastStep ? 'outline' : 'default'

    // Handle conditional buttons separately since they need form state
    if (button.type === 'CommsConditionalButtonRecord') {
      return (
        <F0Button
          key={index}
          onClick={() => handleConditionalButtonClick(button)}
          variant={variant}
          size="md"
          disabled={options.length > 0 && !selectedValue}
          label={button.text}
        />
      )
    }

    // Use CommsButton for all other button types
    return (
      <CommsButton
        key={index}
        button={button}
        variant={variant}
        size="md"
        componentType="in_app_comms_customer_form"
        componentName={componentName}
        onClick={() => {
          if (isLastStep || isCloseButton) {
            onDismiss()
          } else {
            setCurrentStepIndex(currentStepIndex + 1)
          }
        }}
        extraEventProps={{
          stepId: currentStep.stepId,
          selectedOption: selectedValue || '',
        }}
        upsellRequestComponentType="in_app_comms_customer_form"
      />
    )
  }

  return (
    <Box
      background="grey000White"
      borderRadius={{ all: 'abs016' }}
      position="fixed"
      style={{
        bottom: isMobile ? theme.dimensions.s12 : theme.dimensions.s64,
        left: theme.dimensions.s12,
        zIndex: theme.zIndex.modals,
        maxWidth: '216px',
        width: '100%',
        boxShadow: theme.shadows.s50,
      }}
    >
      <Box
        position="absolute"
        style={{
          top: '8px',
          right: '8px',
          zIndex: theme.zIndex.modals + 1,
        }}
      >
        <F0Button
          onClick={handleClose}
          variant="ghost"
          size="sm"
          icon={Cross}
          hideLabel
          round
          label="Close"
          style={{
            backgroundColor: theme.colors.grey000White,
            borderRadius: theme.dimensions.s8,
            opacity: '0.7',
          }}
        />
      </Box>
      <Box padding="s12" gap="s12">
        <F0AvatarModule module={moduleAvailableToModuleId(content.moduleIcon)} size="lg" />

        <Box gap="s8">
          {currentStep.title ? (
            <Box gap="s4">
              <Text.Normal size="200" weight="semibold" color="grey1000">
                {currentStep.title}
              </Text.Normal>
              <Text.Normal size="200" color="grey1000">
                {currentStep.text}
              </Text.Normal>
            </Box>
          ) : (
            <Text.Normal size="200" weight="semibold" color="grey1000">
              {currentStep.text}
            </Text.Normal>
          )}

          {options?.length > 0 && (
            <Box gap="s8" paddingTop="s8">
              {options.map((option: FormOption) => (
                <Box key={option.value} flexDirection="row" gap="s8">
                  <RadioButton
                    value={option.value}
                    checked={selectedValue === (option.internalValue || option.value)}
                    onChange={() => handleOptionChange(option.internalValue || option.value)}
                    size="small"
                  />
                  <Text.Normal size="100" color="grey800">
                    {option.value}
                  </Text.Normal>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box flexDirection="row" justifyContent="flexEnd" alignItems="center">
          <Box flexDirection="row" gap="s12">
            {currentStep.buttons.map((button: InAppCommsButton, index: number) =>
              renderButton(button, index)
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default CustomerForm
